import { useQuery } from '@tanstack/react-query';

import type {
  AssetPool,
  AssetPoolFilters,
  CrewPool,
  CrewPoolFilters,
  PlannerActivity,
  PlannerActivityDetail,
  PlannerActivityListFilters,
  PlannerExecutionLine,
  SchedulingGridFilters,
  UnplannedWorkFilters,
  UnplannedWorkItem,
} from '../types';
import { plannerApi } from './client';
import { plannerKeys } from './keys';

/**
 * `useSchedulingGrid` — the scheduling grid's primary data source.
 *
 * Returns the activities scheduled within `[from, to]` for the current view.
 * Mode (`crew` vs `asset` axis) is applied client-side after fetch — the API
 * doesn't pivot data so we keep the cache flat and let the grid component
 * decide its row pivot.
 */
export function useSchedulingGrid(filters: SchedulingGridFilters) {
  return useQuery<PlannerActivity[]>({
    queryKey: plannerKeys.schedulingGrid(filters),
    queryFn: ({ signal }) => plannerApi.getSchedulingGrid(filters, signal),
    staleTime: 30_000,
  });
}

/**
 * `usePlannerActivities` — lean list of activities for a date range. Used by
 * the Home dashboard summary and the Execution review query.
 */
export function usePlannerActivities(filters: PlannerActivityListFilters) {
  return useQuery<PlannerActivity[]>({
    queryKey: plannerKeys.activityList(filters),
    queryFn: ({ signal }) => plannerApi.listActivities(filters, signal),
    staleTime: 30_000,
  });
}

export function usePlannerActivity(id: string | undefined) {
  return useQuery<PlannerActivityDetail>({
    queryKey: id ? plannerKeys.activityDetail(id) : plannerKeys.activityDetails(),
    queryFn: () => plannerApi.getActivity(id as string),
    enabled: !!id,
  });
}

/**
 * `useUnplannedWork` — backlog queue rendered in the left-rail panel. Filter
 * application is performed client-side (mirrors the legacy behaviour).
 *
 * Optional second arg `{ enabled }` lets callers suppress the fetch when
 * they have an alternative source (e.g. the dispatch board's GetDay
 * composite already returns the pre-filtered unplanned list in real-API
 * mode). Defaults to enabled so existing callers (planner-page) keep
 * fetching unchanged.
 */
export function useUnplannedWork(
  filters: UnplannedWorkFilters = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery<UnplannedWorkItem[]>({
    queryKey: plannerKeys.unplannedList(filters),
    queryFn: ({ signal }) => plannerApi.listUnplannedWork(filters, signal),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

/**
 * `useAssetPool` — assets available on a given day. Drives the right-sidebar
 * asset chip list on the scheduling grid.
 */
export function useAssetPool(filters: AssetPoolFilters | undefined) {
  return useQuery<AssetPool>({
    queryKey: filters ? plannerKeys.assetPool(filters) : plannerKeys.assetPools(),
    queryFn: ({ signal }) =>
      plannerApi.getAssetPool(filters as AssetPoolFilters, signal),
    enabled: !!filters?.date,
    staleTime: 60_000,
  });
}

/**
 * `useCrewPool` — crew members available on a given day.
 */
export function useCrewPool(filters: CrewPoolFilters | undefined) {
  return useQuery<CrewPool>({
    queryKey: filters ? plannerKeys.crewPool(filters) : plannerKeys.crewPools(),
    queryFn: ({ signal }) =>
      plannerApi.getCrewPool(filters as CrewPoolFilters, signal),
    enabled: !!filters?.date,
    staleTime: 60_000,
  });
}

/**
 * `useExecutionLines` — execution-line list for a single activity. Drives the
 * Execution page detail and the in-grid hover preview.
 */
export function useExecutionLines(activityId: string | undefined) {
  return useQuery<PlannerExecutionLine[]>({
    queryKey: activityId
      ? plannerKeys.executionLines(activityId)
      : [...plannerKeys.all, 'execution-lines', 'idle'],
    queryFn: () => plannerApi.listExecutionLines(activityId as string),
    enabled: !!activityId,
  });
}
